import { useEffect, useRef, useState } from 'react';
import {
  ApiError,
  commitLog,
  deleteLog,
  listLogs,
  type SenpaiLog,
} from '../lib/api.js';
import { useFlywheel } from '../lib/flywheel.js';
import {
  DAYS,
  EVENT_TYPE_LABEL,
  ITINERARY,
  TIME_SLOT_LABEL,
  TIME_SLOT_ORDER,
  type ItineraryEvent,
} from '../lib/itinerary.js';
import type { User } from '../lib/users.js';

interface Props {
  user: User;
}

interface Selection {
  joining: boolean;
  intent: string;
}

/**
 * Pre-visit planning surface. The user (Sora-like — applied, prepping
 * to leave) sees the cohort's full 7-day itinerary in a calendar grid
 * and picks which sessions she wants to attend, with a free-text
 * "なぜ参加したいか" note per pick.
 *
 * Each pick is persisted as a SenpaiLog with phase='before' and a
 * deterministic id (`plan-<event_id>`) so re-saves overwrite. This is
 * the same corpus alumni write reflections into — the unified design
 * means Miki can match with Sora and read her plan-stage entries via
 * the existing chat retrieval path.
 *
 * Save semantics:
 *   - Toggle ON  → immediate POST /log/commit (no embedding while
 *                  intent is empty; embedding lands when intent is
 *                  added on blur)
 *   - Intent blur → POST /log/commit with body, embedding fills in
 *   - Toggle OFF → DELETE /log/{senpaiId}/{logId}
 *
 * Layout: a 7-column day grid for wide screens (each column = one day,
 * stacked AM/PM/EVE blocks). Collapses to a vertical day-list on
 * narrow screens.
 */
export function UserPlanning({ user }: Props) {
  const [selections, setSelections] = useState<Map<string, Selection>>(new Map());
  const [openId, setOpenId] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { emit } = useFlywheel();

  // Hydrate from the SENPAI_LOG corpus on mount: any logs whose id
  // begins with 'plan-' are calendar-managed plan entries.
  useEffect(() => {
    let cancelled = false;
    listLogs(user.id)
      .then((r) => {
        if (cancelled) return;
        const next = new Map<string, Selection>();
        for (const log of r.logs) {
          if (!log.id.startsWith('plan-')) continue;
          const eventId = log.id.slice('plan-'.length);
          next.set(eventId, { joining: true, intent: log.body_markdown ?? '' });
        }
        setSelections(next);
        setLoaded(true);
      })
      .catch((err) => {
        if (cancelled) return;
        // 404-on-no-logs is fine; the user may not have planned yet.
        if (err instanceof ApiError && err.status === 404) {
          setLoaded(true);
          return;
        }
        setError(err instanceof ApiError ? err.userMessage : String(err));
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user.id]);

  const buildDraft = (event: ItineraryEvent, intent: string): SenpaiLog => {
    return {
      id: `plan-${event.id}`,
      senpai_id: user.id,
      ...(user.visit_cohort ? { visit_cohort: user.visit_cohort } : {}),
      phase: 'before',
      day_label: event.title,
      tags: [event.type, 'plan'],
      takeaways: [],
      body_markdown: intent.trim(),
      provenance: 'alumni_authored',
    } as SenpaiLog;
  };

  const persistJoin = async (event: ItineraryEvent, intent: string) => {
    setError(null);
    setSaving(true);
    try {
      await commitLog(buildDraft(event, intent));
      // Pre-visit user is "candidate" in the flywheel topology; her
      // plans flow toward operator (signal) and toward future candidates
      // (corpus). The pulse here surfaces the operator-bound flow.
      emit({
        kind: 'SENPAI_LOG_APPENDED',
        actor: 'candidate',
        flowsTo: 'operator',
        note: `${user.display_name}が「${event.title}」を計画に追加`,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : String(err));
    } finally {
      setSaving(false);
    }
  };

  const persistDelete = async (event: ItineraryEvent) => {
    setError(null);
    setSaving(true);
    try {
      await deleteLog(user.id, `plan-${event.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : String(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleJoining = (event: ItineraryEvent) => {
    const cur = selections.get(event.id);
    if (cur?.joining) {
      // Toggle off
      setSelections((prev) => {
        const m = new Map(prev);
        m.delete(event.id);
        return m;
      });
      void persistDelete(event);
    } else {
      const intent = cur?.intent ?? '';
      setSelections((prev) => {
        const m = new Map(prev);
        m.set(event.id, { joining: true, intent });
        return m;
      });
      void persistJoin(event, intent);
    }
  };

  const setIntent = (eventId: string, intent: string) => {
    setSelections((prev) => {
      const cur = prev.get(eventId) ?? { joining: false, intent: '' };
      const m = new Map(prev);
      m.set(eventId, { ...cur, intent });
      return m;
    });
  };

  // Save the intent text on blur — that's when the user has finished
  // editing one card, and the most natural moment to embed.
  const commitIntent = async (event: ItineraryEvent) => {
    const cur = selections.get(event.id);
    if (!cur?.joining) return;
    await persistJoin(event, cur.intent);
  };

  const joiningCount = [...selections.values()].filter((s) => s.joining).length;
  const intentCount = [...selections.values()].filter((s) => s.joining && s.intent.trim()).length;

  return (
    <div className="main">
      <div className="container container-wide">
        <h1>あなたの旅をつくる</h1>
        <p className="lede">
          {user.display_name}さん、出発までの数週間で、行きたいセッションを選んでみてください。
          「なぜ参加したいか」を一言だけでも書いておくと、現地で何を持ち帰るかの解像度が変わります。
        </p>

        {error && <div className="error">{error}</div>}

        <div className="planning-summary">
          <span>
            <strong>{joiningCount}</strong> セッション選択中
          </span>
          <span className="muted">
            (うち <strong>{intentCount}</strong> 件に意図を記入)
          </span>
          {saving && <span className="muted planning-saving">保存中…</span>}
        </div>

        {!loaded && <div className="muted center loading-block">読み込んでいます...</div>}

        {loaded && (
          <div className="calendar-grid">
            {DAYS.map((day) => (
              <DayColumn
                key={day}
                day={day}
                selections={selections}
                openId={openId}
                onOpen={setOpenId}
                onToggle={toggleJoining}
                onIntent={setIntent}
                onIntentBlur={commitIntent}
              />
            ))}
          </div>
        )}

        <p className="footnote footnote-trailing">
          ※ 計画は保存されます。あなたが書いた「なぜ」は、これから候補者を選ぶ後輩の対話の手がかりにもなります。
        </p>
      </div>
    </div>
  );
}

interface DayColumnProps {
  day: number;
  selections: Map<string, Selection>;
  openId: string | null;
  onOpen: (id: string | null) => void;
  onToggle: (event: ItineraryEvent) => void;
  onIntent: (id: string, intent: string) => void;
  onIntentBlur: (event: ItineraryEvent) => void;
}

function DayColumn({
  day,
  selections,
  openId,
  onOpen,
  onToggle,
  onIntent,
  onIntentBlur,
}: DayColumnProps) {
  const events = ITINERARY.filter((e) => e.day === day);
  if (events.length === 0) {
    return (
      <div className="day-column">
        <div className="day-header">Day {day}</div>
        <p className="muted day-empty">予定なし</p>
      </div>
    );
  }
  return (
    <div className="day-column">
      <div className="day-header">Day {day}</div>
      {TIME_SLOT_ORDER.map((slot) => {
        const slotEvents = events.filter((e) => e.slot === slot);
        if (slotEvents.length === 0) return null;
        return (
          <div key={slot} className="slot-block">
            <div className="slot-label">{TIME_SLOT_LABEL[slot]}</div>
            {slotEvents.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                selection={selections.get(event.id) ?? null}
                expanded={openId === event.id}
                onClick={() => onOpen(openId === event.id ? null : event.id)}
                onToggle={() => onToggle(event)}
                onIntent={(text) => onIntent(event.id, text)}
                onIntentBlur={() => onIntentBlur(event)}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

interface EventCardProps {
  event: ItineraryEvent;
  selection: Selection | null;
  expanded: boolean;
  onClick: () => void;
  onToggle: () => void;
  onIntent: (text: string) => void;
  onIntentBlur: () => void;
}

function EventCard({
  event,
  selection,
  expanded,
  onClick,
  onToggle,
  onIntent,
  onIntentBlur,
}: EventCardProps) {
  const joining = selection?.joining ?? false;
  // Track the intent text the user landed on when the card was last
  // shown — only fire onIntentBlur if it changed, to avoid an empty
  // re-save round-trip every time the user toggles a card open/closed.
  const lastSaved = useRef<string>(selection?.intent ?? '');
  useEffect(() => {
    if (selection?.intent !== undefined) lastSaved.current = selection.intent;
  }, [selection?.intent, expanded]);

  return (
    <div className={`event-card ${joining ? 'event-card--joining' : ''}`}>
      <button type="button" className="event-card-head" onClick={onClick} aria-expanded={expanded}>
        <div className="event-card-title">{event.title}</div>
        <div className="event-card-meta">
          <span className={`event-type-tag event-type-tag--${event.type}`}>
            {EVENT_TYPE_LABEL[event.type]}
          </span>
          {joining && <span className="event-joining-tag">参加</span>}
        </div>
      </button>
      {expanded && (
        <div className="event-card-body">
          <p className="event-card-desc">{event.description}</p>
          {event.meta && <p className="event-card-loc">📍 {event.meta}</p>}
          <div className="event-card-actions">
            <button type="button" className={joining ? 'btn' : 'btn-ghost'} onClick={onToggle}>
              {joining ? '✓ 参加する' : '参加してみる'}
            </button>
          </div>
          {joining && (
            <div className="event-card-intent">
              <label className="field-label" htmlFor={`intent-${event.id}`}>
                なぜ参加したいか(任意)
              </label>
              <textarea
                id={`intent-${event.id}`}
                className="textarea"
                rows={2}
                value={selection?.intent ?? ''}
                onChange={(e) => onIntent(e.target.value)}
                onBlur={() => {
                  if ((selection?.intent ?? '') !== lastSaved.current) {
                    lastSaved.current = selection?.intent ?? '';
                    onIntentBlur();
                  }
                }}
                placeholder="例: 自分の Why を整理したい / 英語環境に身を置きたい / 起業の現場を見たい"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
